import { z } from "zod";
import { MODEL_SET_IDS } from "./models.js";
import { SiteIdSchema } from "./sites.js";
/**
 * Plans are code constants (no plan tables, no entitlement keys, no credits). A plan
 * names what it allows and what it costs; subscriptions control access; spending is
 * recorded in USD per operation and capped per run. DRAFT (Phase 1): the lineup, the
 * monthly dollar cap and the Stripe price codes are the owner's decisions and are
 * marked where they are still open.
 */
export const PLAN_IDS = [
    "spark",
    "explorer",
    "builder",
    "frontier",
    "classroom-free",
    "classroom",
    "school",
];
export const PlanIdSchema = z.enum(PLAN_IDS);
export const PlanOfferSchema = z.object({
    /** The Stripe Price lookup_key; one Price per environment carries the same code. */
    code: z.string().min(1),
    interval: z.enum(["month", "year"]),
    amountCents: z.number().int().nonnegative(),
});
export const PlanDefinitionSchema = z.object({
    id: PlanIdSchema,
    siteId: SiteIdSchema,
    name: z.string().min(1),
    rank: z.number().int().positive(),
    isDefault: z.boolean(),
    /** A registry model set, or an explicit list of model ids. */
    models: z.union([z.enum(MODEL_SET_IDS), z.array(z.string().min(1)).min(1)]),
    mcpEnabled: z.boolean(),
    dailyRunCap: z.number().int().positive().nullable(),
    /** OWNER DECISION: null = no monthly cap (only the per-run cap applies). */
    monthlyUsdCap: z.number().nonnegative().nullable(),
    offers: z.array(PlanOfferSchema),
});
/**
 * OWNER DECISION: the dollar cap frozen into every run at launch. One number for every
 * plan until the owner says otherwise; the worker refuses the next paid step at the cap
 * and each adapter sets it natively where it can (session budget, per-response, per-step).
 */
export const DEFAULT_RUN_USD_CAP = 2;
/** V2's lineup carried over without credits; prices are V2's; codes are V5's own Stripe Prices. */
export const PLANS = [
    plan("spark", "myfirstagent", "Spark", 1, true, "essentials", false, 10, [["month", 0]]),
    plan("explorer", "myfirstagent", "Explorer", 2, false, "essentials", true, 50, [
        ["month", 800],
        ["year", 8000],
    ]),
    plan("builder", "myfirstagent", "Builder", 3, false, "standard", true, 100, [
        ["month", 1500],
        ["year", 15000],
    ]),
    plan("frontier", "myfirstagent", "Frontier", 4, false, "frontier", true, 200, [
        ["month", 2500],
        ["year", 25000],
    ]),
    plan("classroom-free", "launchyouragent", "Classroom Free", 1, true, ["claude-haiku-4-5", "claude-haiku-5-5"], false, 20, [["month", 0]]),
    plan("classroom", "launchyouragent", "Classroom", 2, false, "essentials", true, 200, [
        ["month", 1900],
        ["year", 19000],
    ]),
    plan("school", "launchyouragent", "School", 3, false, "standard", true, 400, [
        ["month", 4900],
        ["year", 49000],
    ]),
];
function plan(id, siteId, name, rank, isDefault, models, mcpEnabled, dailyRunCap, offers) {
    const site = siteId === "myfirstagent" ? "mfa" : "lya";
    return {
        id,
        siteId,
        name,
        rank,
        isDefault,
        models,
        mcpEnabled,
        dailyRunCap,
        monthlyUsdCap: null,
        offers: offers.map(([interval, amountCents]) => ({
            code: `v5-${site}-${id}-${interval}`,
            interval,
            amountCents,
        })),
    };
}
export function plansForSite(siteId) {
    return PLANS.filter((entry) => entry.siteId === siteId).sort((a, b) => a.rank - b.rank);
}
export function defaultPlanFor(siteId) {
    const found = plansForSite(siteId).find((entry) => entry.isDefault);
    if (!found)
        throw new Error(`no default plan for ${siteId}`);
    return found;
}
//# sourceMappingURL=plans.js.map